import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UpdateSeasonInput } from '@proref/schemas';
import { listSeasons, createSeason, updateSeason, deleteSeason } from '../services/seasons.service';

// The season calendar — super_admin's admin table AND the Leaderboard's Period card both read
// this key, so a season created/edited on the admin page is reflected on the leaderboard on its
// next mount without a manual refetch wire-up between the two pages.
export function useSeasonsQuery() {
  return useQuery({
    queryKey: ['seasons'],
    queryFn: listSeasons,
    // The calendar changes rarely (a handful of rows a year) — a modest stale window is plenty.
    staleTime: 10 * 60_000,
    // Same reasoning as useSportsQuery/useRulesQuery: a staleTime above the global default plus
    // the persister would otherwise let a reload skip revalidation for up to 10 minutes.
    refetchOnMount: 'always',
  });
}

export function useCreateSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSeason,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['seasons'] });
      // A season just created might be the one covering today — the leaderboard's period='season'
      // board needs to pick it up.
      void qc.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
}

export function useUpdateSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSeasonInput }) => updateSeason(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['seasons'] });
      void qc.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
}

export function useDeleteSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSeason,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['seasons'] });
      void qc.invalidateQueries({ queryKey: ['leaderboard'] });
    },
  });
}
